import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

// External Libraries
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Hooks
import { useModal } from '../../../../hooks';

// Components
import { GIButton } from 'app-components';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import Tooltip from '../../../../components/common/tooltip/Tooltip';

// Types
import { ICheckAptcRequest, IPaymentInfoProps } from '../planSummary.types';

// Utils
import { formatDate } from '../planSummary.utils';

// Styles
import {
    StyledCentered,
    StyledDd,
    StyledDl,
    StyledDt,
    StyledPlanSectionDataContainer,
    StyledPlanSectionHeader,
} from '../plansummary.styled';
import usePlanSummary from '../hooks/usePlanSummary';

// Constants
import { EnrollmentIdType } from '../planSummary.constants';
import { PLAN_TYPE } from '../../../../modules/custom-grouping/modals/modalTypes';

const sumSubsidyAndPremiumCredit = (stateSubsidy, premiumCredit) =>
    [stateSubsidy, premiumCredit].reduce((sum, val) => sum + (val ? parseFloat(val) : 0), 0);

const PaymentInfo = memo(({ plan }: IPaymentInfoProps) => {
    const {
        caseNumber,
        electedAptc,
        premium,
        stateSubsidyAmount,
        statePremiumCreditAmt,
        netPremium,
        premiumEffDate,
        allowAptcAdjustment,
        maxAptc,
    } = plan;
    const { checkAptc } = usePlanSummary();
    const { openModal } = useModal();

    const onAdjustAptcClick = () => {
        const payload: ICheckAptcRequest = {
            caseNumber,
            [plan.typeOfPlan.toUpperCase() === PLAN_TYPE.HEALTH ? EnrollmentIdType.HEALTH : EnrollmentIdType.DENTAL]: plan.enrollmentId,
        };

        checkAptc(payload, plan, openModal);
    };

    return (
        <div data-testid="payment-info">
            <StyledPlanSectionHeader>
                <h5>
                    <FormattedMessage id="plansummary.paymentinformation" />
                </h5>
            </StyledPlanSectionHeader>
            <StyledPlanSectionDataContainer>
                <StyledDl>
                    <StyledDt as="dt" data-testid="monthly-premium-label">
                        <FormattedMessage id="plansummary.monthlypremuim" />:
                    </StyledDt>
                    <StyledDd as="dd" data-testid="monthly-premium-value">
                        ${Number(premium).toFixed(2)}
                    </StyledDd>
                    <ConditionalRendering isTrue={!!electedAptc}>
                        <>
                            <StyledDt as="dt" data-testid="elected-aptc-label">
                                <FormattedMessage id="plansummary.electedAptc" />:
                            </StyledDt>
                            <StyledDd as="dd" data-testid="elected-aptc-value">
                                ${electedAptc}
                            </StyledDd>
                        </>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!!((stateSubsidyAmount !== null && stateSubsidyAmount !== undefined) && !Number(statePremiumCreditAmt))}>
                        <>
                            <StyledDt as="dt" data-testid="state-subsidy-label">
                                <FormattedMessage id="plansummary.stateSubsidyAmount" />:
                            </StyledDt>
                            <StyledDd as="dd" data-testid="state-subsidy-value">
                                ${Number(stateSubsidyAmount).toFixed(2)}
                            </StyledDd>
                        </>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!!(stateSubsidyAmount && statePremiumCreditAmt)}>
                        <>
                            <StyledDt as="dt" data-testid="state-subsidy-label">
                                <FormattedMessage id="plansummary.premiumCredit" />:
                            </StyledDt>
                            <StyledDd as="dd" data-testid="premium-credit-value">
                                ${Number(sumSubsidyAndPremiumCredit(stateSubsidyAmount, statePremiumCreditAmt)).toFixed(2)}
                            </StyledDd>
                        </>
                    </ConditionalRendering>

                    <StyledDt as="dt" data-testid="net-premium-label">
                        <FormattedMessage id="plansummary.netPremium" />:
                    </StyledDt>
                    <StyledDd as="dd" data-testid="net-premium-value">
                        ${Number(netPremium).toFixed(2)}
                    </StyledDd>

                    <ConditionalRendering isTrue={!!premiumEffDate}>
                        <>
                            <StyledDt as="dt" data-testid="premium-effective-date-label">
                                <span>
                                    <FormattedMessage id="plansummary.premiumEffectiveDate" />
                                    <Tooltip
                                        id={
                                            <FontAwesomeIcon
                                                className="premium-date-tooltip padding-left-05"
                                                icon={faQuestionCircle}
                                            />
                                        }
                                        triggerDecorationClassName="no-border"
                                        content="plansummary.premiumEffectiveDateTooltip"
                                    ></Tooltip>
                                    <span>:</span>
                                </span>
                            </StyledDt>
                            <StyledDd as="dd" data-testid="premium-effective-date-value">
                                {formatDate(premiumEffDate)}
                            </StyledDd>
                        </>
                    </ConditionalRendering>
                </StyledDl>
                <ConditionalRendering isTrue={allowAptcAdjustment && maxAptc !== null && Number(maxAptc) !== 0}>
                    <StyledCentered as="div" data-testid="adjust-premium-tax-container">
                        <GIButton
                            id="adjust-premium-tax_btn"
                            data-testid="adjust-premium-tax-button"
                            labelId="plansummary.adjustAptc"
                            onClickHandler={onAdjustAptcClick}
                        />
                    </StyledCentered>
                </ConditionalRendering>
            </StyledPlanSectionDataContainer>
        </div>
    );
});

PaymentInfo.displayName = 'PaymentInfo';
export default PaymentInfo;
